import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Package, Boxes, ShoppingCart, Users, Ticket, Star, Settings as Cog, Store, LogOut } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import { useAuth } from "@/context/AuthContext";
import { api, formatPrice } from "@/lib/api";
import { CHART_HEIGHT } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AdminProducts, AdminInventory } from "./AdminProducts";
import { AdminCoupons } from "./AdminCoupons";

const GOLD = "#D4AF37";
const COLORS = ["#D4AF37", "#000", "#8a7320", "#bfa253", "#5c4d15"];
const STATUSES = ["pending", "processing", "shipped", "delivered"];

const NAV = [
  ["dashboard", "Dashboard", LayoutDashboard],
  ["products", "Products", Package],
  ["inventory", "Inventory", Boxes],
  ["orders", "Orders", ShoppingCart],
  ["customers", "Customers", Users],
  ["coupons", "Coupons", Ticket],
  ["reviews", "Reviews", Star],
  ["settings", "Settings", Cog],
];

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setStats(r.data)).catch(() => {}); }, []);
  if (!stats) return <div className="text-muted-foreground">Loading…</div>;
  const cards = [["Revenue", formatPrice(stats.revenue)], ["Orders", stats.total_orders], ["Products", stats.total_products], ["Customers", stats.total_customers]];
  return (
    <div data-testid="admin-overview">
      <h2 className="font-display text-2xl mb-6">Dashboard</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map(([l, v]) => <div key={l} className="border border-border p-6"><p className="font-display text-3xl">{v}</p><p className="text-sm text-muted-foreground">{l}</p></div>)}
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="border border-border p-6"><h3 className="font-display text-lg mb-4">Products by Category</h3>
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}><BarChart data={stats.by_category}><XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} /><Tooltip /><Bar dataKey="value" fill={GOLD} /></BarChart></ResponsiveContainer>
        </div>
        <div className="border border-border p-6"><h3 className="font-display text-lg mb-4">Catalog Split</h3>
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}><PieChart><Pie data={stats.by_category} dataKey="value" nameKey="name" outerRadius={90} label>{stats.by_category.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip /></PieChart></ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function Orders() {
  const [orders, setOrders] = useState([]);
  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const update = async (id, status) => { await api.patch(`/admin/orders/${id}`, { status }); toast.success("Order updated"); load(); };
  return (
    <div data-testid="admin-orders-section">
      <h2 className="font-display text-2xl mb-6">Orders</h2>
      <div className="border border-border overflow-x-auto"><table className="w-full text-sm">
        <thead className="bg-secondary"><tr className="text-left">{["Tracking", "Email", "Items", "Total", "Payment", "Status"].map((h) => <th key={h} className="p-3 font-medium">{h}</th>)}</tr></thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.order_id} className="border-t border-border" data-testid={`admin-order-${o.tracking_number}`}>
              <td className="p-3 font-mono text-xs">{o.tracking_number}</td><td className="p-3">{o.email}</td><td className="p-3">{o.items?.length}</td>
              <td className="p-3">{formatPrice(o.total)}</td>
              <td className="p-3"><span className={o.payment_status === "paid" ? "text-green-600" : "text-muted-foreground"}>{o.payment_status}</span></td>
              <td className="p-3"><Select value={o.status} onValueChange={(v) => update(o.order_id, v)}><SelectTrigger className="w-32 h-8 rounded-none" data-testid={`order-status-${o.tracking_number}`}><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent></Select></td>
            </tr>
          ))}
          {orders.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-muted-foreground">No orders yet.</td></tr>}
        </tbody>
      </table></div>
    </div>
  );
}

function Customers() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/customers").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div data-testid="admin-customers-section">
      <h2 className="font-display text-2xl mb-6">Customers</h2>
      <div className="border border-border overflow-x-auto"><table className="w-full text-sm">
        <thead className="bg-secondary"><tr className="text-left">{["Name", "Email", "Joined"].map((h) => <th key={h} className="p-3 font-medium">{h}</th>)}</tr></thead>
        <tbody>{rows.map((c) => <tr key={c.id} className="border-t border-border"><td className="p-3">{c.name}</td><td className="p-3">{c.email}</td><td className="p-3">{new Date(c.created_at).toLocaleDateString()}</td></tr>)}
          {rows.length === 0 && <tr><td colSpan={3} className="p-10 text-center text-muted-foreground">No customers yet.</td></tr>}</tbody>
      </table></div>
    </div>
  );
}

function Reviews() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/reviews").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div data-testid="admin-reviews-section">
      <h2 className="font-display text-2xl mb-6">Reviews</h2>
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.id} className="border border-border p-4">
            <div className="flex items-center justify-between">
              <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className={`h-3.5 w-3.5 ${i < r.rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-muted-foreground"}`} />)}</div>
              <span className="text-xs text-muted-foreground">{r.product_name}</span>
            </div>
            <p className="font-display text-lg mt-1">{r.title}</p>
            <p className="text-sm text-muted-foreground">{r.body}</p>
            <p className="text-xs uppercase tracking-wide mt-1">— {r.author}</p>
          </div>
        ))}
        {rows.length === 0 && <p className="text-muted-foreground">No reviews yet.</p>}
      </div>
    </div>
  );
}

function SettingsSection() {
  const [threshold, setThreshold] = useState(5);
  useEffect(() => { api.get("/admin/settings").then((r) => setThreshold(r.data.low_stock_threshold)); }, []);
  const save = async () => { await api.put("/admin/settings", { low_stock_threshold: Number(threshold) }); toast.success("Settings saved"); };
  return (
    <div data-testid="admin-settings-section">
      <h2 className="font-display text-2xl mb-6">Settings</h2>
      <div className="border border-border p-6 max-w-md">
        <label className="text-sm font-medium">Low-stock alert threshold</label>
        <p className="text-xs text-muted-foreground mb-3">Products at or below this quantity are flagged as low stock.</p>
        <div className="flex gap-3">
          <Input type="number" min="0" value={threshold} onChange={(e) => setThreshold(e.target.value)} className="rounded-none w-32" data-testid="settings-threshold" />
          <Button onClick={save} className="rounded-none bg-[#D4AF37] text-black" data-testid="settings-save">Save</Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [section, setSection] = useState("dashboard");

  useEffect(() => { if (!loading && (!user || user.role !== "admin")) nav("/login"); }, [user, loading]);
  if (loading || !user || user.role !== "admin") return <div className="py-24 text-center text-muted-foreground">Loading…</div>;

  const render = () => ({
    dashboard: <Overview />, products: <AdminProducts />, inventory: <AdminInventory />,
    orders: <Orders />, customers: <Customers />, coupons: <AdminCoupons />,
    reviews: <Reviews />, settings: <SettingsSection />,
  }[section]);

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-background">
      <aside className="lg:w-64 border-b lg:border-b-0 lg:border-r border-border bg-[#0a0a0a] text-[#F5F1E8] lg:min-h-screen">
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <Link to="/" className="font-display text-2xl font-bold">GULIT</Link>
          <span className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37]">Admin</span>
        </div>
        <nav className="flex lg:flex-col overflow-x-auto p-2 lg:p-3 gap-1 hide-scrollbar">
          {NAV.map(([key, label, Icon]) => (
            <button key={key} onClick={() => setSection(key)} data-testid={`admin-nav-${key}`}
              className={`flex items-center gap-3 px-4 py-3 text-sm whitespace-nowrap transition ${section === key ? "bg-[#D4AF37] text-black font-medium" : "text-[#cfcabb] hover:bg-white/10"}`}>
              <Icon className="h-4 w-4" />{label}
            </button>
          ))}
        </nav>
        <div className="p-3 lg:mt-auto border-t border-white/10 hidden lg:block">
          <Link to="/" className="flex items-center gap-3 px-4 py-2 text-sm text-[#cfcabb] hover:text-white"><Store className="h-4 w-4" />View Store</Link>
          <button onClick={() => { logout(); nav("/"); }} className="flex items-center gap-3 px-4 py-2 text-sm text-[#cfcabb] hover:text-white w-full" data-testid="admin-logout"><LogOut className="h-4 w-4" />Sign Out</button>
        </div>
      </aside>
      <main className="flex-1 p-5 lg:p-10 overflow-x-hidden">{render()}</main>
    </div>
  );
}
